// Verification fingerprint / "safety number" (ARCHITECTURE.md §6).
//
// Two connected users can read this out loud to each other to confirm they
// hold each other's genuine keys (defends against a man-in-the-middle when a
// Circle ID was shared over a remote channel). Rendered as digit groups —
// language-independent, unlike a word list.
//
// It is symmetric: both sides derive the same number by sorting the two
// Circle IDs before hashing.

import { utf8ToBytes } from './encoding';

const subtle = globalThis.crypto.subtle;
const GROUPS = 8;
const DIGITS_PER_GROUP = 5;

async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle.digest('SHA-256', bytes));
}

/**
 * Compute the shared fingerprint for a pair of Circle IDs.
 * Returns e.g. "01234 56789 ...", 8 groups of 5 digits.
 */
export async function computeFingerprint(circleIdA: string, circleIdB: string): Promise<string> {
  const [low, high] = [circleIdA.trim(), circleIdB.trim()].sort();
  const input = utf8ToBytes(`circle/v1/fingerprint\n${low}\n${high}`);

  // Expand to enough bytes for all digit groups (5 bytes per group).
  const needed = GROUPS * 5;
  const chunks: Uint8Array[] = [];
  for (let counter = 0; chunks.reduce((n, c) => n + c.length, 0) < needed; counter++) {
    const prefixed = new Uint8Array(input.length + 1);
    prefixed[0] = counter;
    prefixed.set(input, 1);
    chunks.push(await sha256(prefixed));
  }
  const material = concat(chunks).slice(0, needed);

  const groups: string[] = [];
  for (let g = 0; g < GROUPS; g++) {
    let value = 0;
    for (let i = 0; i < 5; i++) value = value * 256 + material[g * 5 + i]!;
    const digits = (value % 10 ** DIGITS_PER_GROUP).toString().padStart(DIGITS_PER_GROUP, '0');
    groups.push(digits);
  }
  return groups.join(' ');
}

function concat(parts: Uint8Array[]): Uint8Array {
  let len = 0;
  for (const p of parts) len += p.length;
  const out = new Uint8Array(len);
  let off = 0;
  for (const p of parts) {
    out.set(p, off);
    off += p.length;
  }
  return out;
}
